import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    IconButton,
    DialogContent,
    Button,
    Typography,
    TextField,
    Select,
    MenuItem,
    Autocomplete,
    Checkbox,
    useTheme
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { toast } from 'sonner';
import { BenefitsType, PremiumTypes } from 'Application/models';
import { useAgeRangeStore, useBenefitsStore, useDestinationStore, usePremiumStore } from 'Application/store';
import { useParams } from 'react-router-dom';
import { ThemeMode } from 'config';

interface SetPricings {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
}

const SetPricings: React.FC<SetPricings> = ({ isOpen, setIsOpen }) => {
    const [premiumType, setPremiumType] = useState<string | PremiumTypes>('');
    const [selectedDestinations, setSelectedDestinations] = useState<string[]>([]);
    const { getDestinations, destinationData } = useDestinationStore();
    const { setPremium, isLoading } = usePremiumStore();
    const { ageRangeData, getAgeRange } = useAgeRangeStore();
    const { planID } = useParams<{ planID: string }>();

    const [formData, setFormData] = useState({
        ageRangeID: 0,
        premiumCost: 0,
        minDays: 0,
        maxDays: 0,
        excludedDestinations: [] as string[]
    });

    const { palette } = useTheme();
    const modalTitleColor = palette.mode === ThemeMode.DARK ? '#A0AEC0' : '#4A5568';

    useEffect(() => {
        getAgeRange();
        getDestinations();
    }, [getAgeRange, getDestinations]);

    const handleSelectAll = (event: React.ChangeEvent<{}>, value: string[]) => {
        if (value.includes('Select All')) {
            setSelectedDestinations(
                selectedDestinations.length === destinationData.length ? [] : destinationData.map((item) => item.destinationName)
            );
        } else {
            setSelectedDestinations(value);
        }
    };

    const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        setPremiumType(event.target.value as PremiumTypes);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value
        }));
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        const setPremiumData = {
            ...formData,
            type: premiumType,
            excludedDestinations: selectedDestinations
        };

        try {
            await setPremium(setPremiumData, planID);
            toast.success('Premium values added successfully');
            setIsOpen(false);
            setFormData({
                ageRangeID: 0,
                premiumCost: 0,
                minDays: 0,
                maxDays: 0,
                excludedDestinations: []
            });
            setPremiumType('');
            setSelectedDestinations([]);
        } catch (error) {
            toast.error(`Failed to set premium: ${error}`);
        }
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    width: '70%',
                    maxWidth: '600px'
                }
            }}
        >
            <DialogTitle>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: modalTitleColor }}>Set Plan Pricings</span>
                    <IconButton onClick={handleClose}>
                        <ClearIcon />
                    </IconButton>
                </div>
                <Typography style={{ marginTop: '0.5rem', color: '#718096', fontSize: '0.875rem' }}>
                    Please fill in the following details
                </Typography>
            </DialogTitle>
            <DialogContent>
                <form onSubmit={handleSubmit}>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Age Range
                        </Typography>
                        <Autocomplete
                            id="ageRange"
                            options={ageRangeData}
                            getOptionLabel={(option) => `${option.minAge} - ${option.maxAge}`}
                            onChange={(event, value) => setFormData({ ...formData, ageRangeID: value?.ageRangeID || 0 })}
                            renderInput={(params) => <TextField {...params} required name="ageRange" fullWidth />}
                        />
                    </div>

                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Type of Premium
                        </Typography>
                        <Select value={premiumType} onChange={handleChange} fullWidth displayEmpty>
                            <MenuItem value="" disabled>
                                Select premium type
                            </MenuItem>
                            <MenuItem value={PremiumTypes.DAILY_RANGE}>Daily Range</MenuItem>
                            <MenuItem value={PremiumTypes.GROUP}>Group</MenuItem>
                            <MenuItem value={PremiumTypes.ANNUAL}>Annual</MenuItem>
                        </Select>
                    </div>

                    {premiumType === PremiumTypes.ANNUAL && (
                        <>
                            <div style={{ paddingBottom: '1rem' }}>
                                <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                                    Premium Cost
                                </Typography>
                                <TextField
                                    placeholder="Enter premium cost"
                                    required
                                    name="premiumCost"
                                    variant="outlined"
                                    value={formData.premiumCost}
                                    onChange={handleInputChange}
                                    type="number"
                                    inputProps={{ min: 0 }}
                                    fullWidth
                                />
                            </div>
                        </>
                    )}

                    {premiumType === PremiumTypes.GROUP && (
                        <>
                            <div style={{ paddingBottom: '1rem' }}>
                                <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                                    Premium Cost
                                </Typography>
                                <TextField
                                    placeholder="Enter premium cost"
                                    required
                                    name="premiumCost"
                                    variant="outlined"
                                    value={formData.premiumCost}
                                    onChange={handleInputChange}
                                    type="number"
                                    inputProps={{ min: 0 }}
                                    fullWidth
                                />
                            </div>
                        </>
                    )}

                    {premiumType === PremiumTypes.DAILY_RANGE && (
                        <>
                            <div style={{ paddingBottom: '1rem' }}>
                                <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                                    Premium Cost
                                </Typography>
                                <TextField
                                    placeholder="Enter premium cost"
                                    required
                                    name="premiumCost"
                                    variant="outlined"
                                    value={formData.premiumCost}
                                    onChange={handleInputChange}
                                    type="number"
                                    inputProps={{ min: 0 }}
                                    fullWidth
                                />
                            </div>
                            <div style={{ paddingBottom: '1rem' }}>
                                <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                                    Min Days
                                </Typography>
                                <TextField
                                    placeholder="Enter minimum days"
                                    required
                                    name="minDays"
                                    variant="outlined"
                                    value={formData.minDays}
                                    onChange={handleInputChange}
                                    type="number"
                                    inputProps={{ min: 0 }}
                                    fullWidth
                                />
                            </div>

                            <div style={{ paddingBottom: '1rem' }}>
                                <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                                    Max Days
                                </Typography>
                                <TextField
                                    placeholder="Enter maximum days"
                                    required
                                    name="maxDays"
                                    variant="outlined"
                                    value={formData.maxDays}
                                    onChange={handleInputChange}
                                    type="number"
                                    inputProps={{ min: 0 }}
                                    fullWidth
                                />
                            </div>
                        </>
                    )}

                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Excluded Destinations
                        </Typography>
                        <Autocomplete
                            multiple
                            options={['Select All', ...destinationData.map((item) => item.destinationName)]}
                            value={selectedDestinations}
                            onChange={handleSelectAll}
                            disableCloseOnSelect
                            renderOption={(props, option, { selected }) => (
                                <li {...props}>
                                    <Checkbox
                                        checked={
                                            selected || (option === 'Select All' && selectedDestinations.length === destinationData.length)
                                        }
                                        style={{ marginRight: 8 }}
                                    />
                                    {option}
                                </li>
                            )}
                            renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Select Destinations" />}
                        />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                        <Button
                            onClick={handleClose}
                            variant="outlined"
                            sx={{
                                color: '#4A5568',
                                borderColor: '#4A5568',
                                backgroundColor: '#ffffff'
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="outlined"
                            sx={{
                                backgroundColor: '#673ab7',
                                color: '#ffffff',
                                marginLeft: '1.5rem',
                                '&:hover': {
                                    backgroundColor: '#ffffff',
                                    color: '#673ab7',
                                    borderColor: '#673ab7'
                                },
                                '&.Mui-disabled': {
                                    color: '#ffffff',
                                    backgroundColor: '#98A2B3'
                                }
                            }}
                            disabled={isLoading}
                        >
                            Submit
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default SetPricings;
